import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import {
  Truck,
  ArrowLeft,
  Menu,
  Wifi,
  WifiOff,
  User,
  LogOut,
  Home,
  Package,
  MapPin,
  Navigation,
  RotateCcw
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/shared/components/ui/sheet';
import { useDriver } from '../context/DriverContext';
import { logout, getCurrentUser } from '@/shared/services/api';

const DriverLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOnline, activeRoute, pendingPODs } = useDriver();
  const user = getCurrentUser();

  const menuItems = [
    { name: 'Dashboard', href: '/driver', icon: Home },
    { name: 'Load Route', href: '/driver/route-load', icon: Package },
    { name: 'Active Route', href: '/driver/route-active', icon: Navigation },
    { name: 'Returns', href: '/driver/returns', icon: RotateCcw },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/driver') return 'EzParcel Driver';
    if (path.includes('/route-load')) return 'Load Route';
    if (path.includes('/route-active')) return 'Active Route';
    if (path.includes('/stop-deliver')) return 'Delivery';
    if (path.includes('/stop-failed')) return 'Failed Delivery';
    if (path.includes('/route-complete')) return 'Route Complete';
    if (path.includes('/returns')) return 'Returns';
    return 'EzParcel Driver';
  };

  const canGoBack = location.pathname !== '/driver';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Driver Header - Uber style black */}
      <header className="sticky top-0 z-50 bg-black text-white shadow-md">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            {canGoBack ? (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/driver')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            ) : (
              <Sheet>
                <SheetTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="text-white hover:bg-white/10"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 bg-white">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2 text-black">
                      <Truck className="h-5 w-5" />
                      EzParcel Driver
                    </SheetTitle>
                  </SheetHeader>
                  <nav className="mt-6 space-y-2">
                    {menuItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.href;
                      return (
                        <Button
                          key={item.href}
                          variant={isActive ? 'default' : 'ghost'}
                          className={`w-full justify-start gap-3 ${isActive ? 'bg-black text-white hover:bg-black/90' : 'text-black hover:bg-gray-100'}`}
                          onClick={() => navigate(item.href)}
                        >
                          <Icon className="h-5 w-5" />
                          {item.name}
                        </Button>
                      );
                    })}
                  </nav>
                  <div className="absolute bottom-6 left-4 right-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User className="h-4 w-4" />
                      <span>{user?.full_name || user?.email || 'Driver'}</span>
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full gap-2 border-gray-300 text-black hover:bg-gray-100"
                      onClick={handleLogout}
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            )}
            <h1 className="text-lg font-semibold">{getPageTitle()}</h1>
          </div>
          
          <div className="flex items-center gap-2">
            {activeRoute && (
              <Badge className="bg-green-500 text-white border-0">
                Route Active
              </Badge>
            )}
            {pendingPODs.length > 0 && (
              <Badge className="bg-amber-500 text-white border-0">
                {pendingPODs.length} pending
              </Badge>
            )}
            {isOnline ? (
              <Wifi className="h-5 w-5 text-green-400" />
            ) : (
              <WifiOff className="h-5 w-5 text-red-400" />
            )}
          </div>
        </div>
      </header>

      {/* Offline Banner */}
      {!isOnline && (
        <div className="bg-green-500 text-white text-center py-2 text-sm font-medium">
          You're offline. PODs will sync when connected.
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav className="sticky bottom-0 bg-white border-t border-gray-200 safe-area-pb">
        <div className="flex justify-around py-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href || 
              (item.href !== '/driver' && location.pathname.startsWith(item.href));
            return (
              <Button
                key={item.href}
                variant="ghost"
                size="sm"
                className={`flex-col h-auto py-2 px-3 gap-1 ${isActive ? 'text-black' : 'text-gray-400'}`}
                onClick={() => navigate(item.href)}
              >
                <Icon className={`h-5 w-5 ${isActive ? 'text-green-500' : ''}`} />
                <span className="text-xs">{item.name.split(' ')[0]}</span>
              </Button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default DriverLayout;
